import React, { useState, useEffect } from 'react';

import { Dialog, DialogPanel, PopoverGroup } from '@headlessui/react';
import Icon from '@components/utils/IconWrap';
import { themeChange } from 'theme-change';
import { ApconLogo } from '@assets/icons';
import wwwApconCom from '../../www.apcon.com';
import NavPopover from './NavPopover';
import NavDisclosure from './NavDisclosure';
import { SolutionsDock, CompanyDock, ProductsDock } from './docks';

const Products = [
  {
    name: 'IntellaView Hardware',
    url: wwwApconCom.hardware.intellaview.DEFAULT,
  },
  {
    name: 'IntellaView User Interface',
    url: wwwApconCom.intellaview.gui,
  },
  {
    name: 'IntellaTap-VM',
    url: wwwApconCom.intellaview.intellatapVm,
  },
  {
    name: 'ApconTap',
    url: wwwApconCom.hardware.networkTaps.apconTap,
  },
  {
    name: 'IntellaView Enterprise',
    url: wwwApconCom.intellaview.enterprise,
  },
  {
    name: 'Enterprise Lite',
    url: wwwApconCom.intellaview.enterpriseLite,
  },
  {
    name: 'IntellaView Mobile',
    url: wwwApconCom.intellaview.intellaviewMobile,
  },
  {
    name: 'Legacy Products',
    url: wwwApconCom.legacyProductsPolicy,
  },
];
const Solutions = [
  {
    name: 'Network-wide Packet Visibility',
    url: wwwApconCom.solutions.networkVisibility,
  },
  { name: 'Network Monitoring', url: wwwApconCom.solutions.networkMonitoring },
  { name: 'Network Security', url: wwwApconCom.solutions.networkSecurity },
  {
    name: 'Network Data Compliance',
    url: wwwApconCom.solutions.networkDataCompliance,
  },
  { name: 'Software Services', url: wwwApconCom.solutions.softwareServices },
];
const Company = [
  { name: 'Blog', url: wwwApconCom.blog },
  { name: 'News', url: wwwApconCom.newsroom },
  { name: 'Events', url: wwwApconCom.events },
  { name: 'Careers', url: wwwApconCom.careers },
];

export default function Example() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Load theme from localStorage on component mount
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme') || 'dark';
      document.body.setAttribute('data-theme', savedTheme);
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(savedTheme);
      // Update the checkbox state based on saved theme
      const themeController = document.querySelector('.theme-controller');
      if (themeController) {
        themeController.checked = savedTheme === 'dark';
      }
    }
  }, []);

  const handleThemeChange = e => {
    if (typeof window !== 'undefined') {
      const newTheme = e.target.checked ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      document.body.setAttribute('data-theme', newTheme);
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(newTheme);
      themeChange();
    }
  };

  return (
    <header className="sticky top-0 isolate z-50 bg-base-100">
      {/* desktop menu */}
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between p-3 lg:px-8"
      >
        <div className="flex lg:flex-1">
          <a href={wwwApconCom.homepage} className="-m-1.5 p-1.5 h-10">
            <span className="sr-only">APCON</span>
            <ApconLogo className="h-8 w-auto text-logo " />
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-base-content"
          >
            <span className="sr-only">Open main menu</span>
            <Icon icon="mdi:menu" aria-hidden="true" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          <NavPopover label="Products" items={[]} callToActions={[]}>
            <ProductsDock />
          </NavPopover>

          <NavPopover label="Solutions" items={[]} callToActions={[]}>
            <SolutionsDock />
          </NavPopover>

          <a
            href={wwwApconCom.search}
            className="text-sm/6 font-semibold text-base-content cursor-pointer"
          >
            Resources
          </a>

          <NavPopover label="Company" items={[]} callToActions={[]}>
            <CompanyDock />
          </NavPopover>
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 gap-x-2 lg:justify-end items-center">
          <a
            href={wwwApconCom.contactUs}
            className=" text-sm/6 font-semibold hover:text-primary hover:bg-base-300 transition-all duration-300 text-base-content border border-base-200 bg-base-100 py-1 px-3 flex items-center gap-x-2 rounded-full"
          >
            Contact <Icon icon="ic:round-phone" aria-hidden="true" size="5" />
          </a>
          <a
            href={wwwApconCom.getSupport}
            className="text-sm/6 font-semibold text-primary-content hover:bg-primary/80 bg-primary py-1 px-3 flex items-center gap-x-2 rounded-full"
          >
            Support{' '}
            <Icon
              icon="ion:people-sharp"
              aria-hidden="true"
              className="size-4"
            />
          </a>
          <div className="size-6">
            <label className="swap swap-rotate">
              {/* this hidden checkbox controls the state */}
              <input
                type="checkbox"
                className="theme-controller"
                value="light"
                onChange={handleThemeChange}
              />

              {/* sun icon */}
              <svg
                className="swap-off h-10 w-10 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
              </svg>

              {/* moon icon */}
              <svg
                className="swap-on h-10 w-10 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
              </svg>
            </label>
          </div>
        </div>
      </nav>

      {/* mobile menu */}
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-40" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full transition-transform duration-300 overflow-y-auto bg-base-300 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-base-content/10">
          <div className="flex items-center justify-between">
            <a href={wwwApconCom.homepage} className="-m-1.5 p-1.5 h-8">
              <span className="sr-only">APCON</span>
              <ApconLogo className="h-8 w-auto text-logo" />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-base-content"
            >
              <span className="sr-only">Close menu</span>
              <Icon icon="mdi:close" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-neutral/10">
              <div className="space-y-2 py-6">
                <NavDisclosure label="Product">
                  <a
                    href={wwwApconCom.intellaview.DEFAULT}
                    className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-neutral hover:bg-base-300"
                  >
                    IntellaView Platform
                  </a>
                  {Products.map(item => (
                    <a
                      key={item.name}
                      href={item.url}
                      className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-neutral hover:bg-gray-50"
                    >
                      {item.name}
                    </a>
                  ))}
                </NavDisclosure>
                <NavDisclosure label="Solutions">
                  {Solutions.map(item => (
                    <a
                      key={item.name}
                      href={item.url}
                      className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-neutral hover:bg-base-300"
                    >
                      {item.name}
                    </a>
                  ))}
                </NavDisclosure>
                <a
                  href={wwwApconCom.search}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-base-content hover:bg-base-100"
                >
                  Resources
                </a>
                <NavDisclosure label="Company">
                  {Company.map(item => (
                    <a
                      key={item.name}
                      href={item.url}
                      className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-neutral hover:bg-base-300"
                    >
                      {item.name}
                    </a>
                  ))}
                </NavDisclosure>
              </div>
              <div className="py-6">
                <a
                  href={wwwApconCom.contactUs}
                  className="-mx-3 block rounded-lg px-3 py-2.5 flex flex-row items-center gap-x-2 text-base/7 font-semibold text-base-content hover:bg-base-100"
                >
                  <Icon
                    icon="ic:round-phone"
                    aria-hidden="true"
                    className="size-4 text-neutral"
                  />{' '}
                  Contact
                </a>
                <a
                  href={wwwApconCom.getSupport}
                  className="-mx-3 block rounded-lg px-3 py-2.5 flex flex-row items-center gap-x-2 text-base/7 font-semibold text-base-content hover:bg-base-100"
                >
                  <Icon
                    icon="ion:people-sharp"
                    aria-hidden="true"
                    className="size-4 text-neutral"
                  />{' '}
                  Support
                </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
